/**
 * clientLocation: the client mirror of api/_shared/clientLocation.ts,
 * the one pair of rules for where a client is and where they live, over
 * the record's address rows (AddressRow, src/lib/clientRecord.ts).
 *
 *   currentLocation  the active "currently at" row, else the active
 *                    primary row, else null.
 *   homeAddress      the active primary row, else null. The label is
 *                    never read.
 *   currentlyAt      the current location when it is a different row
 *                    from the home: what every "Currently at" line shows.
 *   homeFact         what Home says: the active primary row; no primary
 *                    when the client has any address row (archived
 *                    included) and no active primary; none only with no
 *                    row at all. The band, the Addresses card's summary
 *                    and the face sheet all read it.
 *
 * Both rules pick by the flags alone, never by row order; a client has at
 * most one active primary and one active current row.
 * tests/ui/clientLocationParity.test.ts proves this file and the server
 * module agree. The copy every surface uses for a client that is not on
 * the map, or is only approximately on it, lives here too.
 */

import type { AddressRow } from '@/lib/clientRecord';
import type { WorkingState } from '@/lib/tenantConfig';

/** The flags the pick reads, and the row's id. */
export type LocationFlagRow = Pick<AddressRow, 'id' | 'isActive' | 'isPrimary' | 'isCurrentLocation'>;

/** The active current-location row, else the active primary row, else null. */
export function currentLocation<R extends LocationFlagRow>(rows: readonly R[]): R | null {
  return rows.find((r) => r.isActive && r.isCurrentLocation) ?? rows.find((r) => r.isActive && r.isPrimary) ?? null;
}

/** The active primary row, else null. */
export function homeAddress<R extends LocationFlagRow>(rows: readonly R[]): R | null {
  return rows.find((r) => r.isActive && r.isPrimary) ?? null;
}

/** The current location when it is a different row from the home; null
 *  when the client is at home or has no current location. */
export function currentlyAt<R extends LocationFlagRow>(rows: readonly R[]): R | null {
  const current = currentLocation(rows);
  return current !== null && current.id !== homeAddress(rows)?.id ? current : null;
}

/** What Home says for a client: the active primary row; 'no_primary' when
 *  the client has any address row (archived included) and no active
 *  primary; 'none' when the client has no address row at all. */
export type HomeFact = { kind: 'home'; row: AddressRow } | { kind: 'no_primary' } | { kind: 'none' };

export function homeFact(rows: readonly AddressRow[]): HomeFact {
  const home = homeAddress(rows);
  if (home !== null) return { kind: 'home', row: home };
  return rows.length > 0 ? { kind: 'no_primary' } : { kind: 'none' };
}

/** Why a client is not on the map (the server's ClientNotPlacedReason).
 *  'not_found_add_state' is an address typed with no state that the map
 *  did not place. */
export type ClientNotPlacedReason = 'no_address' | 'no_primary' | 'not_checked' | 'not_found' | 'not_found_add_state';

/** The plain-words reason for every client reason that names no state,
 *  for every surface that lists a client off the map. */
export const CLIENT_NOT_PLACED_LABEL: Record<Exclude<ClientNotPlacedReason, 'not_found_add_state'>, string> = {
  no_address: 'No address on file',
  no_primary: 'No primary address',
  not_checked: 'Address not checked yet',
  not_found: 'Address not found',
};

/** Why an address the map was asked about holds no point: the reasons
 *  the placement rule gives, and text the directory never looked up. */
export type NotPlacedLabelReason = 'not_found' | 'not_found_add_state' | 'no_street_address' | 'not_checked';

/** Names joined in the order given, never re-sorted: "A", "A or B",
 *  "A, B or C". */
export function joinWithOr(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/** The working states' names joined in the order given (the server's:
 *  most covered counties first), never re-sorted: "A", "A or B",
 *  "A, B or C". */
export function joinStateNames(workingStates: readonly WorkingState[]): string {
  return joinWithOr(workingStates.map((s) => s.name));
}

/** The words for an address the map did not place. The add-state line
 *  names the tenant's working states, from getTenantConfig; it shows only
 *  when no state was typed, which the reason already says. */
export function notPlacedLabel(reason: NotPlacedLabelReason, workingStates: readonly WorkingState[]): string {
  switch (reason) {
    case 'not_found':
      return 'Address not found';
    case 'not_found_add_state':
      return workingStates.length === 0
        ? 'Address not found. Add the state.'
        : `Address not found. If it's outside ${joinStateNames(workingStates)}, add the state.`;
    case 'no_street_address':
      return 'No street address';
    case 'not_checked':
      return 'Address on file, not on the map yet';
  }
}

/** A client's reason in words: CLIENT_NOT_PLACED_LABEL, or the add-state
 *  line naming the working states. */
export function clientNotPlacedLabel(
  reason: ClientNotPlacedReason,
  workingStates: readonly WorkingState[],
): string {
  return reason === 'not_found_add_state' ? notPlacedLabel(reason, workingStates) : CLIENT_NOT_PLACED_LABEL[reason];
}

/** Appended to a pin's title when its precision is 'zip': an approximate
 *  location. */
export const APPROXIMATE_SUFFIX = ' · approximate location';

/** A chosen client's chip line when only the town or ZIP was found. */
export const APPROXIMATE_LOCATION_TEXT = 'Approximate location';

/** What the record says for Home when the client has address rows but
 *  no active primary (homeFact's no_primary). */
export const NO_PRIMARY_TEXT = 'No primary address';

/** The intake card's Location line when the current location row names
 *  neither a town nor a state. */
export const LOCATION_TOWN_MISSING = 'Town not on file';

/** The intake card's Location line when the prospects list could not be read. */
export const LOCATION_LOAD_FAILED = 'Could not load';

/** The intake card's link to the Team map. */
export const SHOW_ON_MAP_LABEL = 'Show on map';

/** The Team map's query parameter that opens Place a client on one intake
 *  card. Read once when the map mounts, then removed from the URL. */
export const TEAM_MAP_PROSPECT_PARAM = 'prospect';

/** A current location's town and state in plain words: each part trimmed,
 *  empty parts dropped, the rest joined with ", ", and the first letter of
 *  the result capitalised. The stored values are never rewritten. Empty
 *  when neither part is on file. */
export function locationWords(city: string | null, state: string | null): string {
  const words = [city, state]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part !== '')
    .join(', ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The Team map with Place a client opened on one intake card. */
export function showOnMapHref(tenantSlug: string, workItemId: string): string {
  return `/t/${tenantSlug}/team?tab=map&${TEAM_MAP_PROSPECT_PARAM}=${encodeURIComponent(workItemId)}`;
}
